export function fmtHrs(h) {
  if (!h && h !== 0) return null;
  const hh = Math.floor(h);
  const mm = Math.round((h - hh) * 60);
  return `${hh}h ${String(mm).padStart(2,'0')}m`;
}

export function fmtDate(s) {
  if (!s) return '';
  const d = new Date(s + 'T12:00:00');
  return d.toLocaleDateString('en-US', { weekday:'short', month:'short', day:'numeric', year:'numeric' });
}

// Motor time WHILE MOVING: the manually-entered motor_hrs if present, otherwise the
// engine-hours delta (total engine run) capped at total moving time — motoring can't
// exceed the time actually under way. Returns null when unknown.
// Motor time = engine hours run during the passage (engine_end - engine_start),
// capped at moving time. The boat cannot have motored longer than it was moving.
export function movingMotorHrs(e) {
  const moving = parseFloat(e.moving_hrs);
  if (e.engine_start == null || e.engine_start === '' || e.engine_end == null || e.engine_end === '') return null;
  let motor = Math.max(Number(e.engine_end) - Number(e.engine_start), 0);
  if (isNaN(motor)) return null;
  if (moving > 0) motor = Math.min(motor, moving);
  return motor;
}

// Sailing % = (1 - motor time / moving time) * 100.
// Whatever moving time was not under engine was sailed.
export function sailingPct(e) {
  const moving = parseFloat(e.moving_hrs);
  if (!moving || moving <= 0) return null;
  const motor = movingMotorHrs(e);
  if (motor == null) return null;
  return Math.round((1 - motor / moving) * 100);
}

// Total (dock-to-dock) hours: prefer time_at_sea (hrs), else parse total_time "HH:MM".
export function totalHrs(e) {
  if (e.time_at_sea != null && e.time_at_sea !== '') { const n = Number(e.time_at_sea); if (!isNaN(n)) return n; }
  if (e.total_time) { const m = /(\d{1,2}):(\d{2})/.exec(e.total_time); if (m) return (+m[1]) + (+m[2]) / 60; }
  if (e.moving_hrs != null && e.moving_hrs !== '') { const n = Number(e.moving_hrs); if (!isNaN(n)) return n; }
  return null;
}

// A stay is time aboard at one location: at anchor or at the dock, no passage.
export function isStayEntry(e) {
  const t = e.trip_type;
  return t === 'At Anchor' || t === 'At Dock' || t === 'Day at Anchor';
}

// Nights aboard for an entry (stays and overnight passages alike).
export function nightsAboard(e) {
  const n = Number(e.nights);
  return isNaN(n) ? 0 : Math.max(n, 0);
}

// A qualifying day at sea = 3+ hours under way. Nights spent aboard at anchor
// or on the dock are time on the boat, but they are not sea time, so they are
// tracked separately rather than counted as qualifying days.
export function qualifiesSeaDay(e) {
  if (isStayEntry(e)) return false;
  const h = totalHrs(e);
  return h != null && h >= 3;
}

// Everyone aboard for an entry: captain first, then crew and guests (deduped).
export function entryCrew(e) {
  const list = [e.captain || 'Frederick'];
  const add = (s) => { if (s) s.split(',').map(x => x.trim()).filter(Boolean).forEach(n => list.push(n)); };
  add(e.crew); add(e.guests);
  return [...new Set(list)];
}

// Aggregate qualifying sea days per person across all entries.
export function seaDaysByPerson(entries) {
  const map = {};
  (entries || []).forEach(e => {
    const qual = qualifiesSeaDay(e);
    const nights = nightsAboard(e);
    if (!qual && !nights) return;
    const cap = e.captain || 'Frederick';
    entryCrew(e).forEach(p => {
      if (!map[p]) map[p] = { person: p, days: 0, nights: 0, captainDays: 0, nm: 0, trips: [] };
      if (qual) {
        map[p].days += 1;
        if (p === cap) map[p].captainDays += 1;
        map[p].nm += Number(e.nm) || 0;
        map[p].trips.push(e);
      }
      map[p].nights += nights;
    });
  });
  return Object.values(map).sort((a, b) => b.days - a.days || b.nights - a.nights);
}

// ── Boat totals: baselines reconciled against the logbook ────────
// Engine/generator hours are ODOMETERS: the true reading is the highest
// value ever logged, never an older "current" that has since been passed.
// Hull distance is CUMULATIVE: the baseline covers everything before the
// logbook began, then every logged trip adds to it.

export function engineHours(entries = [], settings = {}) {
  const logged = entries
    .map(e => Number(e.engine_end))
    .filter(n => !isNaN(n) && n > 0);
  return Math.max(
    Number(settings.engine_current) || 0,
    Number(settings.engine_baseline) || 0,
    ...(logged.length ? logged : [0]),
  ) || null;
}

export function genHours(entries = [], settings = {}) {
  const logged = entries
    .map(e => Number(e.gen_end))
    .filter(n => !isNaN(n) && n > 0);
  return Math.max(
    Number(settings.gen_current) || 0,
    Number(settings.gen_baseline) || 0,
    ...(logged.length ? logged : [0]),
  ) || null;
}

// Baseline (miles before the logbook) + every nm logged since.
export function hullNm(entries = [], settings = {}) {
  const base = Number(settings.hull_nm_baseline) || 0;
  // Imported historical entries pre-date the baseline, which already accounts
  // for their miles. Only miles logged AFTER the baseline add to the total.
  const logged = entries.reduce((sum, e) => sum + (e.imported ? 0 : (Number(e.nm) || 0)), 0);
  return Math.round(base + logged);
}

// A race can be flagged two ways: the Race? field, or trip_type = 'Race'.
// Treat either as authoritative so filters and labels never disagree.
export function isRaceEntry(e) {
  return !!e.race || e.trip_type === 'Race';
}

// ── Fresh water: latest gauge reading from the logbook ───────────
// The entry's water_used is the gauge in litres (0 = full). Combined
// capacity is 445 L (210 fore + 235 aft). Returns the most recent
// entry that recorded a reading.
export const WATER_CAPACITY_L = 445;

export function latestWater(entries = []) {
  const withReading = (entries || []).filter(e => e.water_used != null && e.water_used !== '');
  if (!withReading.length) return null;
  const e = withReading.reduce((a, b) => (new Date(b.date) > new Date(a.date) ? b : a));
  const used = Math.max(Number(e.water_used) || 0, 0);
  return {
    used,
    remaining: Math.max(WATER_CAPACITY_L - used, 0),
    pct: Math.min(100, Math.round((used / WATER_CAPACITY_L) * 100)),
    date: e.date,
    location: e.destination || e.dest_name || e.departure || '',
    toppedOff: used === 0,
  };
}

// ── Carry-over readings for a new entry ──────────────────────────
// Meters and gauges roll forward: a new entry should start where the
// last one that recorded each value left off. The newest entry may have
// blanks (e.g. no genset run), so we look back per-field to the most
// recent entry that actually has a number, rather than one "prev entry".
export function latestReadings(entries = [], settings = {}) {
  const byDate = [...entries].filter(e => !e.guest_sailing)
    .sort((a, b) => new Date(b.date) - new Date(a.date));
  const lastOf = (field) => {
    for (const e of byDate) {
      const v = e[field];
      if (v != null && v !== '' && !isNaN(Number(v))) return Number(v);
    }
    return null;
  };
  return {
    engine_end: lastOf('engine_end') ?? (Number(settings.engine_baseline) || 2940),
    gen_end:    lastOf('gen_end')    ?? (Number(settings.gen_baseline) || 1130),
    log_nm:     lastOf('log_nm')     ?? (Number(settings.hull_nm_baseline) || 12934),
    water_used: lastOf('water_used'), // null if never recorded (tanks unknown)
  };
}

// Whole nights between two ISO dates (arrival → departure). Same day = 0.
export function nightsBetween(arrival, departure) {
  if (!arrival || !departure) return null;
  const a = new Date(arrival + 'T00:00:00');
  const d = new Date(departure + 'T00:00:00');
  if (isNaN(a) || isNaN(d)) return null;
  const nights = Math.round((d - a) / 86400000);
  return nights >= 0 ? nights : null;
}

// ── Voyage rollups ───────────────────────────────────────────────
// All stats for a voyage, computed from its assigned entries.
export function voyageStats(entries = []) {
  const list = entries || [];
  const passages = list.filter(e => !isStayEntry(e));
  const stays = list.filter(isStayEntry);
  const isAnchor = (e) => e.trip_type === 'At Anchor' || e.trip_type === 'Day at Anchor';
  const isDock = (e) => e.trip_type === 'At Dock';

  const distance = Math.round(list.reduce((s, e) => s + (Number(e.nm) || 0), 0));
  const seaHrs = list.reduce((s, e) => s + (totalHrs(e) || 0), 0);
  const nights = list.reduce((s, e) => s + nightsAboard(e), 0);
  const anchored = stays.filter(isAnchor).reduce((s, e) => s + nightsAboard(e), 0);
  const docked = stays.filter(isDock).reduce((s, e) => s + nightsAboard(e), 0);

  const dates = list.map(e => e.date).filter(Boolean).sort();
  const firstDate = dates[0] || null;
  const lastDate = dates[dates.length - 1] || null;

  return {
    distance,
    seaHrs,
    passages: passages.length,
    stays: stays.length,
    nights,
    anchored,
    docked,
    entries: list.length,
    firstDate,
    lastDate,
  };
}

// Calendar days a voyage spans (inclusive). Uses the voyage's own dates when
// set, else the range of its entries.
export function voyageDuration(voyage, entries = []) {
  const s = voyage?.start_date || voyageStats(entries).firstDate;
  const e = voyage?.end_date || voyageStats(entries).lastDate;
  if (!s || !e) return null;
  const days = Math.round((new Date(e + 'T00:00:00') - new Date(s + 'T00:00:00')) / 86400000) + 1;
  return days > 0 ? days : null;
}

// First departure → last destination, for the route summary line.
export function voyageRoute(entries = []) {
  const byDate = [...(entries || [])].filter(e => e.date).sort((a, b) => new Date(a.date) - new Date(b.date));
  if (!byDate.length) return '';
  const from = byDate[0].departure || byDate[0].destination || '';
  const lastWithDest = [...byDate].reverse().find(e => e.destination || e.dest_name);
  const to = lastWithDest ? (lastWithDest.destination || lastWithDest.dest_name) : '';
  if (from && to && from !== to) return `${from} → ${to}`;
  return from || to || '';
}

// ── Stitch a voyage's legs into one continuous track for the voyage map ──
// Concatenates each leg's stored GPS track in date order, and returns the
// per-leg stop points (departures/arrivals/anchorages) for markers.
export function voyageTrack(entries = []) {
  const byDate = [...(entries || [])].filter(e => e.date).sort((a, b) => new Date(a.date) - new Date(b.date));
  const track = [];
  const stops = [];
  byDate.forEach(e => {
    const t = Array.isArray(e.track) ? e.track : null;
    if (t && t.length > 1) {
      // avoid a duplicate point where one leg ends and the next begins
      const start = (track.length && samePt(track[track.length - 1], t[0])) ? 1 : 0;
      for (let i = start; i < t.length; i++) track.push(t[i]);
    }
    // a stop marker from explicit start/end coords if present
    const pt = firstCoord(e);
    if (pt) stops.push({ ...pt, label: e.title || e.destination || e.departure || '', date: e.date, stay: (e.trip_type === 'At Anchor' || e.trip_type === 'At Dock' || e.trip_type === 'Day at Anchor') });
  });
  return { track, stops };
}

function samePt(a, b) {
  return a && b && Math.abs(a.lat - b.lat) < 1e-5 && Math.abs(a.lon - b.lon) < 1e-5;
}
function firstCoord(e) {
  if (e.start_lat != null && e.start_lon != null) return { lat: Number(e.start_lat), lon: Number(e.start_lon) };
  if (Array.isArray(e.track) && e.track.length) return { lat: e.track[0].lat, lon: e.track[0].lon };
  if (e.end_lat != null && e.end_lon != null) return { lat: Number(e.end_lat), lon: Number(e.end_lon) };
  return null;
}

// ── Full voyage stat breakdown for the Stats tab ─────────────────
// Real-data-only: fields that aren't logged come back null so the UI can hide
// the whole category. FUEL_CRUISE_GPH/tank match the fuel-since-fill logic.
const V_CRUISE_GPH = 1.04;   // 2400 rpm
const V_CRUISE_KTS = 6.3;

function num(v) { const n = Number(v); return isNaN(n) ? null : n; }
function fmtHM(h) {
  if (h == null) return null;
  const H = Math.floor(h), M = Math.round((h - H) * 60);
  return `${H}h${M ? ' ' + M + 'm' : ''}`;
}
function hasSYC(e) {
  const hay = [e.destination, e.dest_name, e.overnight_notes, e.comments].filter(Boolean).join(' ');
  return /\bSYC\b/i.test(hay);
}

export function voyageStatsFull(entries = []) {
  const list = entries || [];
  const passages = list.filter(e => !isStayEntry(e));
  const stays = list.filter(isStayEntry);
  const isAnchor = (e) => e.trip_type === 'At Anchor' || e.trip_type === 'Day at Anchor';
  const isDock = (e) => e.trip_type === 'At Dock';

  // ── Distance ──
  const legNm = passages.map(e => ({ nm: num(e.nm), name: e.title || e.destination || '', date: e.date })).filter(x => x.nm != null && x.nm > 0);
  const totalNm = Math.round(legNm.reduce((s, x) => s + x.nm, 0));
  const longestNm = legNm.length ? legNm.reduce((a, b) => b.nm > a.nm ? b : a) : null;
  const shortestNm = legNm.length ? legNm.reduce((a, b) => b.nm < a.nm ? b : a) : null;
  const avgNm = legNm.length ? +(totalNm / legNm.length).toFixed(1) : null;

  // ── Time ──
  const legHrs = passages.map(e => ({ h: num(e.time_at_sea), name: e.title || e.destination || '', date: e.date })).filter(x => x.h != null && x.h > 0);
  const totalHrsV = legHrs.reduce((s, x) => s + x.h, 0);
  const longestHr = legHrs.length ? legHrs.reduce((a, b) => b.h > a.h ? b : a) : null;
  const shortestHr = legHrs.length ? legHrs.reduce((a, b) => b.h < a.h ? b : a) : null;
  const avgHr = legHrs.length ? totalHrsV / legHrs.length : null;
  const daysUnderway = new Set(passages.filter(e => e.date).map(e => e.date)).size;
  const daysRest = new Set(stays.filter(e => e.date).map(e => e.date)).size;

  // ── Speed (only if logged) ──
  const sogVals = list.map(e => num(e.max_sog)).filter(v => v != null && v > 0);
  const topSogEntry = list.filter(e => num(e.max_sog)).sort((a, b) => num(b.max_sog) - num(a.max_sog))[0] || null;
  const topSog = topSogEntry ? { v: num(topSogEntry.max_sog), name: topSogEntry.title || topSogEntry.destination || '', date: topSogEntry.date } : null;
  const avgSogVals = passages.map(e => num(e.avg_sog)).filter(v => v != null && v > 0);
  const avgSog = avgSogVals.length ? +(avgSogVals.reduce((s, v) => s + v, 0) / avgSogVals.length).toFixed(1) : null;
  // best day's run
  const byDay = {};
  passages.forEach(e => { if (e.date && num(e.nm)) byDay[e.date] = (byDay[e.date] || 0) + num(e.nm); });
  const bestDay = Object.values(byDay).length ? Math.round(Math.max(...Object.values(byDay))) : null;

  // ── Sailing (only if sail_pct logged) ──
  const sailLegs = passages.filter(e => num(e.sail_pct) != null && num(e.nm) != null);
  let sailPct = null, sailedNm = null, motoredNm = null;
  if (sailLegs.length) {
    const distNm = sailLegs.reduce((s, e) => s + num(e.nm), 0);
    sailedNm = Math.round(sailLegs.reduce((s, e) => s + num(e.nm) * num(e.sail_pct) / 100, 0));
    motoredNm = Math.round(distNm - sailedNm);
    sailPct = distNm ? Math.round((sailedNm / distNm) * 100) : null;
  }

  // ── Nights / Stays ──
  const nightsAnchor = stays.filter(isAnchor).reduce((s, e) => s + nightsAboard(e), 0);
  const dockStays = stays.filter(isDock);
  const nightsSYC = dockStays.filter(hasSYC).reduce((s, e) => s + nightsAboard(e), 0);
  const nightsOther = dockStays.filter(e => !hasSYC(e)).reduce((s, e) => s + nightsAboard(e), 0);
  const nightsTotal = nightsAnchor + nightsSYC + nightsOther;
  const stopCount = stays.length;

  // ── Engine / Gen (last − first reading across legs that carry them) ── est.
  const engReads = list.map(e => num(e.engine_end)).filter(v => v != null && v > 0).sort((a, b) => a - b);
  const engStartReads = list.map(e => num(e.engine_start)).filter(v => v != null && v > 0).sort((a, b) => a - b);
  const engFirst = engStartReads.length ? engStartReads[0] : (engReads.length ? engReads[0] : null);
  const engLast = engReads.length ? engReads[engReads.length - 1] : null;
  const engineRun = (engFirst != null && engLast != null && engLast >= engFirst) ? +(engLast - engFirst).toFixed(1) : null;
  const genReads = list.map(e => num(e.gen_end)).filter(v => v != null && v > 0).sort((a, b) => a - b);
  const genStartReads = list.map(e => num(e.gen_start)).filter(v => v != null && v > 0).sort((a, b) => a - b);
  const genFirst = genStartReads.length ? genStartReads[0] : (genReads.length ? genReads[0] : null);
  const genLast = genReads.length ? genReads[genReads.length - 1] : null;
  const genRun = (genFirst != null && genLast != null && genLast >= genFirst) ? +(genLast - genFirst).toFixed(1) : null;

  // ── Fuel (est. from engine hours × cruise burn) ──
  const fuelBurned = engineRun != null ? +(engineRun * V_CRUISE_GPH).toFixed(1) : null;
  const nmPerGal = (fuelBurned && fuelBurned > 0 && totalNm) ? +(totalNm / fuelBurned).toFixed(1) : null;

  return {
    distance: legNm.length ? {
      total: totalNm, passages: legNm.length,
      longest: longestNm && { v: +longestNm.nm.toFixed(1), name: longestNm.name, date: longestNm.date },
      shortest: shortestNm && { v: +shortestNm.nm.toFixed(1), name: shortestNm.name, date: shortestNm.date },
      avg: avgNm,
    } : null,
    time: legHrs.length ? {
      total: fmtHM(totalHrsV), totalRaw: totalHrsV,
      longest: longestHr && { v: fmtHM(longestHr.h), name: longestHr.name, date: longestHr.date },
      shortest: shortestHr && { v: fmtHM(shortestHr.h), name: shortestHr.name, date: shortestHr.date },
      avg: fmtHM(avgHr), daysUnderway, daysRest,
    } : null,
    speed: (avgSog != null || topSog || bestDay != null) ? {
      avg: avgSog, top: topSog, bestDay,
    } : null,
    sailing: sailPct != null ? { pct: sailPct, sailedNm, motoredNm } : null,
    nights: (nightsTotal > 0 || stopCount > 0) ? {
      anchor: nightsAnchor, syc: nightsSYC, other: nightsOther, total: nightsTotal, stops: stopCount,
    } : null,
    engine: (engineRun != null || genRun != null) ? {
      engineRun, genRun, engFirst, engLast, estimated: true,
    } : null,
    fuel: fuelBurned != null ? {
      burned: fuelBurned, gph: V_CRUISE_GPH, nmPerGal, estimated: true,
    } : null,
  };
}

// ── "Since PMF was last underway" ────────────────────────────────
// The most recent entry that was actually under way (a passage/sail/race, not a
// stay). Returns the entry plus a parsed Date of its departure moment so the
// banner can count elapsed time. Uses depart_time if present, else noon.
export function lastUnderway(entries = []) {
  const underway = (entries || []).filter(e => e.date && !isStayEntry(e));
  if (!underway.length) return null;
  const withTs = underway.map(e => ({ e, ts: entryDepartTs(e) })).filter(x => x.ts != null);
  if (!withTs.length) return null;
  withTs.sort((a, b) => b.ts - a.ts);
  return { entry: withTs[0].e, ts: withTs[0].ts };
}

function entryDepartTs(e) {
  if (!e.date) return null;
  const t = (e.depart_time && /^\d{1,2}:\d{2}/.test(e.depart_time)) ? e.depart_time.slice(0, 5) : '12:00';
  const d = new Date(`${e.date}T${t}:00`);
  return isNaN(d.getTime()) ? null : d.getTime();
}

// Whether the most recent underway entry is today (still "out").
export function isOutToday(entries = []) {
  const lu = lastUnderway(entries);
  if (!lu) return false;
  return lu.entry.date === new Date().toISOString().slice(0, 10);
}

// Format an elapsed-milliseconds gap as "6 days 14 hrs" / "14 hrs 3 min".
export function sinceParts(ms) {
  if (ms == null || ms < 0) return null;
  const mins = Math.floor(ms / 60000);
  const days = Math.floor(mins / 1440);
  const hrs = Math.floor((mins % 1440) / 60);
  const m = mins % 60;
  if (days > 0) return { a: days, aU: days === 1 ? 'day' : 'days', b: hrs, bU: hrs === 1 ? 'hr' : 'hrs' };
  if (hrs > 0) return { a: hrs, aU: hrs === 1 ? 'hr' : 'hrs', b: m, bU: 'min' };
  return { a: m, aU: 'min', b: null, bU: null };
}

// ── Annual summary (per-year rollups + year list) ────────────────
const A_CRUISE_GPH = 1.04;   // 2400 rpm, for est. fuel

const yearOf = (d) => (d && d.length >= 4) ? d.slice(0, 4) : null;
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

// The list of years that have any entry, newest first.
export function logYears(entries = []) {
  const ys = new Set();
  (entries || []).forEach(e => { const y = yearOf(e.date); if (y) ys.add(y); });
  return [...ys].sort((a, b) => b - a);
}

// One year's numbers. real-data-only: fields with no data come back null.
export function yearSummary(entries = [], year) {
  const list = (entries || []).filter(e => yearOf(e.date) === String(year));
  if (!list.length) return null;
  const passages = list.filter(e => !isStayEntry(e));
  const num = (v) => { const n = Number(v); return isNaN(n) ? null : n; };

  const distance = Math.round(passages.reduce((s, e) => s + (num(e.nm) || 0), 0));
  const seaHrs = passages.reduce((s, e) => s + (totalHrs(e) || 0), 0);
  const daysUnderway = new Set(passages.filter(e => e.date).map(e => e.date)).size;
  const nights = list.reduce((s, e) => s + nightsAboard(e), 0);
  const races = list.filter(isRaceEntry).length;
  const solo = list.filter(e => e.solo === 'Y').length;

  // engine/gen run this year (last − first reading), est.
  const eng = list.map(e => num(e.engine_end)).filter(v => v != null && v > 0).sort((a, b) => a - b);
  const engStart = list.map(e => num(e.engine_start)).filter(v => v != null && v > 0).sort((a, b) => a - b);
  const engFirst = engStart.length ? engStart[0] : (eng.length ? eng[0] : null);
  const engLast = eng.length ? eng[eng.length - 1] : null;
  const engineRun = (engFirst != null && engLast != null && engLast >= engFirst) ? +(engLast - engFirst).toFixed(1) : null;
  const fuelBurned = engineRun != null ? Math.round(engineRun * A_CRUISE_GPH) : null;

  // speed / sailing (only if logged)
  const sogList = list.filter(e => num(e.max_sog)).sort((a, b) => num(b.max_sog) - num(a.max_sog));
  const topSog = sogList.length ? num(sogList[0].max_sog) : null;
  const longP = passages.filter(e => num(e.nm)).sort((a, b) => num(b.nm) - num(a.nm))[0] || null;
  const longestNm = longP ? { v: +num(longP.nm).toFixed(1), name: longP.title || longP.destination || '', date: longP.date } : null;

  // monthly nm (12) + top month
  const monthly = Array(12).fill(0);
  passages.forEach(e => { const m = e.date && +e.date.slice(5, 7) - 1; if (m >= 0 && num(e.nm)) monthly[m] += num(e.nm); });
  const hasMonthly = monthly.some(v => v > 0);
  const topMonthIdx = hasMonthly ? monthly.indexOf(Math.max(...monthly)) : -1;

  // first / last sail dates
  const sailDates = passages.filter(e => e.date).map(e => e.date).sort();
  const firstSail = sailDates[0] || null;
  const lastSail = sailDates[sailDates.length - 1] || null;

  return {
    year: String(year),
    distance, seaHrs, daysUnderway, nights,
    passages: passages.length, races, solo,
    engineRun, fuelBurned,
    topSog, longestNm,
    monthly: hasMonthly ? monthly.map(v => Math.round(v)) : null,
    topMonth: topMonthIdx >= 0 ? { name: MONTHS[topMonthIdx], nm: Math.round(monthly[topMonthIdx]) } : null,
    firstSail, lastSail,
  };
}

// All years summarized, newest first, each with a delta vs the prior year.
export function annualSummaries(entries = []) {
  const years = logYears(entries).slice().sort(); // ascending for delta calc
  const out = years.map(y => yearSummary(entries, y)).filter(Boolean);
  for (let i = 0; i < out.length; i++) {
    const prev = i > 0 ? out[i - 1] : null;
    out[i].deltaNm = prev ? out[i].distance - prev.distance : null;
    out[i].deltaDays = prev ? out[i].daysUnderway - prev.daysUnderway : null;
  }
  return out.reverse(); // newest first for display
}
